import { useObjectMemo } from '@monkvision/common';
import { MonkApiConfig, useMonkApi } from '@monkvision/network';
import { useEffect, useRef, useCallback } from 'react';
import { useMonitoring } from '@monkvision/monitoring';

const CAPTURE_DURATION = 'capture_duration';

/**
 * Parameters of the useTrackDuration hook.
 */
export interface SessionTimeTrackerParams {
  /**
   * The inspection ID.
   */
  inspectionId: string;
  /**
   * The api config used to communicate with the API.
   */
  apiConfig: MonkApiConfig;
  /**
   * Boolean indicating if the inspection is completed or not.
   */
  isInspectionCompleted: boolean;
  /**
   * Interval in milliseconds for the heartbeat to update the duration.
   */
  heartbeatInterval?: number;
}

/**
 *  Handle used to manage the capture duration.
 */
export interface HandleSessionTimeTracker {
  /**
   * Callback to update the capture duration in the API.
   */
  updateDuration: () => Promise<number>;
}

/**
 * Custom hook used to track the duration of an inspection session.
 */
export function useTrackDuration({
  heartbeatInterval = 30000,
  apiConfig,
  inspectionId,
  isInspectionCompleted,
}: SessionTimeTrackerParams): HandleSessionTimeTracker {
  const startTimeRef = useRef<number>(Date.now());
  const totalActiveTimeRef = useRef<number>(0);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdateRef = useRef<boolean>(false);
  const captureDurationRef = useRef<number>(0);
  const isActive = useRef<boolean>(true);

  const { updateAdditionalData } = useMonkApi(apiConfig);
  const { handleError } = useMonitoring();

  const pauseTracking = useCallback((): void => {
    if (isActive.current) {
      isActive.current = false;
    }
  }, [isActive.current]);

  const resumeTracking = useCallback((): void => {
    if (!isActive.current) {
      startTimeRef.current = Date.now();
      isActive.current = true;
    }
  }, [isActive.current]);

  const updateDuration = useCallback(async (): Promise<number> => {
    if (isInspectionCompleted || pendingUpdateRef.current) {
      return 0;
    }
    totalActiveTimeRef.current += (Date.now() - startTimeRef.current) / 1000;
    try {
      pendingUpdateRef.current = true;
      let existingDuration = 0;
      await updateAdditionalData({
        id: inspectionId,
        callback: (existingData) => {
          existingDuration = existingData?.[CAPTURE_DURATION]
            ? (existingData?.[CAPTURE_DURATION] as number)
            : 0;
          return {
            ...existingData,
            capture_duration: existingDuration + totalActiveTimeRef.current,
          };
        },
      });
      captureDurationRef.current = existingDuration + totalActiveTimeRef.current;
      startTimeRef.current = Date.now();
      totalActiveTimeRef.current = 0;
      return captureDurationRef.current;
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      pendingUpdateRef.current = false;
    }
  }, [
    updateAdditionalData,
    inspectionId,
    captureDurationRef.current,
    startTimeRef.current,
    totalActiveTimeRef.current,
    isInspectionCompleted,
    handleError,
  ]);

  useEffect(() => {
    if (isInspectionCompleted) {
      return undefined;
    }
    const activityEvents: (keyof DocumentEventMap | keyof WindowEventMap)[] = [
      'touchstart',
      'touchmove',
      'touchend',
      'click',
      'scroll',
      'keydown',
      'mousedown',
      'mousemove',
    ];

    const handleActivity = (): void => {
      resumeTracking();
    };

    const handleBeforeUnload = (): void => {
      updateDuration();
    };

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        updateDuration();
        pauseTracking();
      } else {
        resumeTracking();
      }
    };

    heartbeatTimerRef.current = setInterval(() => {
      if (isActive.current) {
        updateDuration();
      }
    }, heartbeatInterval);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });

      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
      }
    };
  }, [
    pauseTracking,
    resumeTracking,
    updateDuration,
    heartbeatInterval,
    isInspectionCompleted,
    captureDurationRef.current,
  ]);

  return useObjectMemo({ updateDuration });
}
